import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetData,
  type SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetError,
  type SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetResponse,
  searchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGet,
} from '../../generated';

export type SearchIdeasQueryVariables = SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetData['path'] &
  SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetData['query'];

export type SearchIdeasQuery = {
  data: SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchIdeasQuery = <
  TData = SearchIdeasQuery,
  TError = SearchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGetError,
>(
  { portfolio_id, image_id, limit, offset, sort_by, sort_direction }: SearchIdeasQueryVariables,
  options?: Omit<UseQueryOptions<SearchIdeasQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchIdeasQuery, TError, TData>({
    queryKey: ['SearchIdeas', { portfolio_id, image_id, limit, offset, sort_by, sort_direction }],
    queryFn: async () => {
      const { data, response } = await searchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGet({
        path: { portfolio_id, image_id },
        query: { limit, offset, sort_by, sort_direction },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchIdeasQuery.getKey = ({
  portfolio_id,
  image_id,
  limit,
  offset,
  sort_by,
  sort_direction,
}: SearchIdeasQueryVariables) => ['SearchIdeas', { portfolio_id, image_id, limit, offset, sort_by, sort_direction }];

useSearchIdeasQuery.fetcher = (
  { portfolio_id, image_id, limit, offset, sort_by, sort_direction }: SearchIdeasQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchIdeasV1PortfoliosPortfolioIdAssetsImagesImageIdIdeasGet({
      path: { portfolio_id, image_id },
      query: { limit, offset, sort_by, sort_direction },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
